import type { DeliveryMetrics, ImpromptuPronunciationResult, ImpromptuReport } from "./types";

/**
 * Every learner-facing string in the impromptu feature.
 *
 * The API speaks in codes so the schema can stay stable; nothing in the interface
 * should show a learner a code. Interface language is English by decision — only
 * transcript quotes and the sharper version stay in the practice language.
 *
 * It all lives in one module because the same sentences were previously written
 * three times across the landing page, the setup panel, and the in-attempt
 * sidebar, and the copies had already drifted apart. A learner reading two of
 * those pages would reasonably conclude they were being offered two different
 * products.
 *
 * This module deliberately imports no runtime values, only types, so it can be
 * executed directly with `node --experimental-strip-types` when checking wording.
 */

type DimensionBand = ImpromptuReport["message"]["relevance"]["band"];

/**
 * "Not enough evidence" is not a grade. It means the model made a claim it could
 * not tie to exact words in the transcript, so we withheld the band rather than
 * report a judgement we cannot show the learner.
 */
export const IMPROMPTU_BAND_LABELS: Record<DimensionBand, string> = {
  strong: "Strong",
  developing: "Developing",
  needs_focus: "Needs focus",
  insufficient: "Not enough evidence",
};

type PaceBand = DeliveryMetrics["paceBand"];

export const IMPROMPTU_PACE_BAND_LABELS: Record<PaceBand, string> = {
  measured: "measured",
  steady: "steady",
  quick: "quick",
  insufficient: "hard to judge",
};

export const IMPROMPTU_PRONUNCIATION_ISSUE_LABELS = {
  mispronunciation: "Sound did not match the model",
  unexpected_break: "Unexpected break before this word",
  missing_break: "A break may be missing",
  monotone: "Intonation stayed on one level",
} as const;

type PronunciationUnavailableReason = Extract<
  ImpromptuPronunciationResult,
  { status: "unavailable" }
>["reason"];

/**
 * A learner whose report expected pronunciation and got nothing deserves to know
 * which kind of nothing it was. "Unsupported" means their browser could not give
 * us usable audio and never will, so telling them it "was not available for this
 * attempt" would invite them to keep retrying something that cannot work.
 */
export const IMPROMPTU_PRONUNCIATION_UNAVAILABLE_COPY: Record<
  PronunciationUnavailableReason,
  string
> = {
  disabled: "Pronunciation feedback is switched off. The rest of your report is unaffected.",
  unsupported:
    "This browser could not convert your recording into the format pronunciation scoring needs, so we skipped that part. The rest of your report is unaffected.",
  unavailable:
    "Pronunciation feedback was not available for this attempt. The rest of your report is unaffected.",
};

/**
 * What the learner can do about a failed attempt.
 *
 * This is a presentation concern rather than a transport one — the analyze route
 * reports whether an attempt is `retryable`, and this decides which of two very
 * different offers that becomes. Sending the same recording again is not the same
 * favour as asking someone to speak for a minute a second time, so the interface
 * should not word them identically.
 */
export type ImpromptuRecoveryKind =
  /** Our side failed after we had usable audio, so resend the same recording. */
  | "resend"
  /** The audio itself was the problem, so record again under the same attempt. */
  | "rerecord"
  /** Nothing usable survives; start a fresh attempt on the same prompt. */
  | "restart"
  /** Retrying cannot work — the browser or the feature will not support it. */
  | "none";

/**
 * `none` is excluded rather than mapped to null. A failure that cannot be retried
 * renders no action at all, so there is no label to write — and typing the map
 * over every kind would hand callers a `string | null` they would then have to
 * decide what to do with, reintroducing the question this taxonomy already
 * answered.
 */
export const IMPROMPTU_RECOVERY_ACTION_LABELS: Record<
  Exclude<ImpromptuRecoveryKind, "none">,
  string
> = {
  resend: "Send it again",
  rerecord: "Record again",
  restart: "Try again",
};

/**
 * Failures that happen on the learner's device, before or instead of a reply from
 * our own routes. Kept as an enumerable set rather than inline strings so that
 * every way an attempt can end has wording someone has actually read.
 */
export type ImpromptuLocalFailure =
  | "unsupported_browser"
  | "microphone_blocked"
  | "microphone_lost"
  | "recorder_unavailable"
  | "recording_failed"
  | "recording_too_short"
  | "network"
  | "invalid_report";

export const IMPROMPTU_LOCAL_FAILURE_COPY: Record<ImpromptuLocalFailure, string> = {
  unsupported_browser: "Audio recording is not supported in this browser.",
  microphone_blocked: "Microphone access is blocked. Allow access in your browser, then try again.",
  microphone_lost: "The microphone is no longer available. Try again.",
  recorder_unavailable: "This browser could not start the recording.",
  recording_failed: "The recording stopped unexpectedly. Please try again.",
  // Deliberately without a numeral: the minimum lives in `config.ts` and a
  // hard-coded "five seconds" here would quietly contradict it the first time
  // that constant moves.
  recording_too_short: "That was too short to analyse. Speak for a little longer and try again.",
  network: "The upload was interrupted. Check your connection and try again.",
  invalid_report: "The feedback response was not valid. Please try again.",
};

/**
 * The promise not to fake precision, in one wording.
 *
 * It was previously three: the attempt sidebar said "We do not score confidence
 * or judge your accent", the pronunciation card said "not an accent grade", and
 * the landing page said "No stars. No confidence score." Three phrasings of one
 * commitment read as three separate hedges, and a learner comparing them would
 * fairly wonder which of the three we actually meant.
 */
/**
 * What happens to the recording, in one wording, for the same reason.
 */
const PRIVACY = "Your recording is processed for this report and is not saved to your account.";

export const IMPROMPTU_COPY = {
  privacy: PRIVACY,

  errors: {
    /**
     * Only for a failure the analyze route reported without a message we can
     * show, which should not happen — the route sends learner-facing text with
     * every code. It exists so a schema change cannot produce a blank screen.
     */
    unknownFailure: "Feedback could not be completed. Please try again.",
  },

  chrome: {
    /** Shown in the embedded surface, where "back" returns to the mode picker. */
    backToModes: "Speaking modes",
    backToHomeLabel: "Back to ChickyTutor",
    surfaceName: "Impromptu practice",
  },

  /**
   * The public marketing surface at `/impromptu`, which stands in for the ready
   * state when the feature is not embedded in the practice shell.
   *
   * The headline is the product thesis from `DESIGN.md`, not a fourth tagline.
   * This feature had accumulated three — "Speak without a script", "Say it before
   * you overthink it", and "Make your point without a script" — one per surface,
   * none of them the line the rest of the product already uses. The thesis also
   * happens to describe this mode exactly: it is the only surface where all three
   * beats are a single sitting.
   *
   * Two sections follow the hero rather than four. The stats band is gone because
   * its numbers were either invented or about to change ("4 languages at launch"
   * is a fact about us, not a reason to speak today), and the practice-loop
   * section is gone because its three steps restated the headline at length and
   * closed on a fortune cookie.
   *
   * There are no footer link labels here. The page uses the shared
   * `LandingFooter`, which owns its own links; keeping a second "Privacy" and
   * "Terms" pair in this module would only guarantee that one of the two
   * eventually points somewhere the other does not.
   */
  landing: {
    eyebrow: "Impromptu language practice",
    heading: "Find the words when the question changes.",
    subheading:
      "Choose a topic in the language you're learning, record one take, and see what to change before you try it again.",

    navHowItWorks: "How it works",
    navTopics: "Topics",
    navFeedback: "The report",
    navFaq: "Questions",
    homeLabel: "ChickyTutor home",
    startPractice: "Start practising",

    languageLine:
      "Practise in 14 languages, from English and Spanish to Mandarin Chinese and Hindi.",

    storyEyebrow: "Beyond rehearsed phrases",
    storyHeading: "Knowing the answer is different from saying it.",
    storyBody:
      "A conversation changes direction. Someone asks why. The sentence you practised no longer fits. Impromptu practice trains the language you need in that next moment.",
    scenarios: [
      ["Real conversations", "Stay in the language when the topic changes."],
      ["Work and study", "Explain a choice without translating every sentence first."],
      ["Speaking exams", "Turn an unfamiliar prompt into a connected response."],
      ["Travel", "Describe what you need when the memorised phrase is too narrow."],
    ],

    transformationEyebrow: "One take at a time",
    transformationHeading: "See what changes on the second take.",
    transformationBody:
      "ChickyTutor shows what it heard, scores the answer, and points to one change worth making while the first take is still fresh.",
    exampleLabel: "Example practice loop",
    examplePromptLabel: "Prompt",
    examplePrompt: "Should people have one meeting-free day each week?",
    exampleFirstLabel: "First take",
    exampleFirst: "I support meeting-free days.",
    exampleFeedbackLabel: "Feedback",
    exampleFeedback: "Your position is clear. Add the benefit, then close on it.",
    exampleSecondLabel: "Next take",
    exampleSecond: "I support meeting-free days because they protect focused work.",

    feedbackEyebrow: "Your speaking report",
    feedbackHeading: "Turn your transcript into a scorecard you can use.",
    feedbackSubheading:
      "Start with the words ChickyTutor heard. Then see percentage scores, delivery measurements and coaching tied to exact parts of your answer.",
    feedbackPreviewTitle: "What every report covers",
    feedbackPreviewCaption: "Ready after each take",
    feedbackRows: [
      ["Transcript first", "Read the words that every score and coaching note is based on."],
      ["Percentage scorecard", "Compare relevance, structure, grammar and vocabulary at a glance."],
      [
        "Delivery and pronunciation",
        "See pace, pauses, repeated wording and pronunciation scores.",
      ],
      [
        "A better next take",
        "Keep one strength, fix one weak spot, then answer the same prompt again.",
      ],
    ],

    faqEyebrow: "Questions before you speak",
    faqHeading: "What to expect from impromptu practice.",
    faqs: [
      [
        "What is impromptu speaking practice?",
        "It trains you to organise an answer and speak without reading a script. You choose a prompt, start when you are ready, and talk for up to 90 seconds.",
      ],
      [
        "How does ChickyTutor analyse my speech?",
        "ChickyTutor transcribes your recording, measures delivery, and scores relevance, structure, grammar and vocabulary. Pronunciation scores are added when your browser and selected language support them.",
      ],
      [
        "Which languages can I practise?",
        "Choose from 14 languages, including English, Spanish, Dutch, Mandarin Chinese, Hindi and Swedish. Every language has its own 32-prompt library.",
      ],
      [
        "Can I choose my own speaking topic?",
        "Yes. Shuffle the topic library, ask ChickyTutor to make a prompt about a subject, or write the exact question you want to practise.",
      ],
      [
        "Does ChickyTutor save my recording?",
        "No. Your recording is processed to create the report and is not saved to your account.",
      ],
    ],

    closingHeading: "Practise the answer you cannot rehearse.",
    closingBody: "Pick a topic, record one take and see exactly where your answer lands.",
    closingAction: "Record my first take",
  },

  /**
   * The embedded setup surface, reached by someone who has already chosen this
   * mode from the picker. No eyebrow and no pitch: the shell header already names
   * the surface, and the heading's job is to say what to do next.
   */
  setup: {
    heading: "Pick a Topic!",
    subheading:
      "Speak for up to 90 seconds and get a transcript, percentage scorecard and a sharper version of your answer.",
    languageLabel: "Speaking language",
    levelLabel: "Your level",
  },

  /**
   * The prompt chooser, which renders on both the landing page and the embedded
   * setup panel — so its wording is shared surface, not marketing copy.
   *
   * It used to be called the "Topic machine", with a "Spin the library" tab, a
   * "Spin topic" lever, a "Spinning" busy state and a "Surprise me" filter: a slot
   * machine, applied to language practice, four times over. The interaction is
   * worth keeping — one topic at a time, easy to replace — but the honest verb for
   * it is shuffle, and the "all" filter honestly means any category.
   */
  promptStudio: {
    panelLabel: "Choose a topic",
    /** e.g. "32 prompts in this language". */
    topicCount: (count: number) => `${count} prompts in this language`,

    sourceGroupLabel: "Where the topic comes from",
    sourceLibrary: "Topic library",
    sourceGenerate: "Make a prompt",
    sourceWrite: "Write my own",

    categoryGroupLabel: "Topic category",
    categoryAny: "Any topic",
    /** The eyebrow above a prompt with no category of its own. */
    ownTopicLabel: "Your topic",
    madeForYou: "Made for you",

    shuffle: "Another topic",
    shuffleBusy: "Finding",
    shuffleLabel: "Show another topic",
    shuffleBusyLabel: "Finding another topic",

    /**
     * Both durations are arguments for the same reason as the attempt checklist:
     * `config.ts` owns the numbers, and a second copy here would disagree with it
     * silently.
     */
    speakSeconds: (maxDurationSeconds: number) => `Up to ${maxDurationSeconds} seconds to speak`,
    start: "Record my answer",

    /** e.g. "Browse all 32 topics". */
    browseAll: (count: number) => `Browse all ${count} topics`,

    generateHeading: "Ask for a topic of your own.",
    generateBody:
      "Name a subject and we write one prompt for it, at the language and level you chose.",
    generateSubjectLabel: "Subject",
    generateSubjectOptional: "(optional)",
    generateSubjectPlaceholder: "A work dilemma, an upcoming trip, a new habit",
    generateAction: "Generate my prompt",
    generateBusy: "Making your prompt",

    writeHeading: "Bring your own question.",
    writeBody:
      "Practise a meeting question, an exam topic, or anything you want to answer without a script.",
    writeLabel: "Your prompt",
    writePlaceholder: "What do you want to practise answering?",

    /** e.g. "Write between 12 and 220 characters." */
    lengthError: (minimum: number, maximum: number) =>
      `Write between ${minimum} and ${maximum} characters.`,
    generateFailed: "We could not make a prompt just now.",
    generateInvalid: "The new prompt was not valid. Try again.",
  },

  /**
   * The searchable full library below the hero.
   *
   * Its strings lived inside the component, which is how it ended up telling the
   * learner to "Show 16 more topics" while the page counted a different number,
   * and how its eyebrow came to be an instruction to the reader ("Pick, do not
   * scroll forever") rather than a label for what follows. The page size and the
   * remaining count are arguments so the button can only ever offer what is
   * actually left.
   */
  topicLibrary: {
    eyebrow: "The full library",
    heading: "Every prompt, in one place.",
    /** e.g. "Search all 32 prompts in your language, or narrow it to the kind of thinking you want to practise." */
    subheading: (count: number) =>
      `Search all ${count} prompts in your language, or narrow it to the kind of thinking you want to practise.`,
    searchLabel: "Search topics",
    searchPlaceholder: "Search topics",
    filterGroupLabel: "Filter the topic library",
    /** e.g. "All 32". The count sits in the chip because the chips beside it carry theirs. */
    filterAll: (count: number) => `All ${count}`,
    practise: "Practise",
    /** e.g. "Show 16 more" — never more than the number that remain. */
    showMore: (count: number) => `Show ${count} more`,
    empty: "No topic matches that search. Try a shorter word or another category.",
  },

  /**
   * Mid-attempt. This surface previously opened with an eyebrow, a headline and a
   * subheading before the prompt, which meant the largest text on screen while
   * someone was recording was a pitch for the feature they were already using.
   * The prompt is the only thing here a learner needs to read, so the prompt is
   * the heading.
   */
  attempt: {
    promptLabel: "Your prompt",
    requestingMicrophone: "Allow microphone access to begin",
    requestingMicrophoneHint: "Use your normal speaking voice.",
    cancel: "Cancel",
    /** Rendered after the remaining-time clock, e.g. "01:12 left". */
    timeRemainingSuffix: "left",
    recordingHint: "Keep going. Finish the thought in your own words.",
    stopAndAnalyse: "Stop and get feedback",
    analysingHeading: "Building your report",
    analysingHint:
      "We are transcribing your answer, measuring your delivery and preparing feedback for your next take.",
    failureHeading: "That attempt did not finish",
    backToSetup: "Back to setup",
    /**
     * Shown on the recovery button while a rate limit is still in force. Takes the
     * seconds rather than naming a fixed wait, because the wait comes from the
     * route's `Retry-After` and changes with the bucket.
     */
    retryAvailableIn: (seconds: number) => `Try again in ${seconds}s`,
    /**
     * The accessible name for that same held button. Deliberately without the
     * number: the visible label ticks once a second inside a `role="alert"`
     * region, and a name that changed with it would be read out every second.
     */
    retryHeldLabel: (action: string) => `${action}. Available in a moment`,
    /**
     * Takes the two durations rather than naming them, so the one place they are
     * defined stays the one place they can change. Writing "15 seconds" here
     * would contradict `config.ts` silently the first time either constant moves,
     * which is the same trap `recording_too_short` is worded to avoid.
     *
     * Only shown before recording starts. Once the clock is running these three
     * lines describe a decision the learner has already made.
     */
    checklist: (maxDurationSeconds: number) => [
      `Up to ${maxDurationSeconds} seconds of speaking, uninterrupted.`,
      "Your transcript and scorecard arrive after you stop.",
    ],
    detailsHeading: "Practice details",
    adjustHeading: "Adjust your practice",
    languageDetailLabel: "Language",
    levelDetailLabel: "Level",
  },

  report: {
    eyebrow: "Your report",
    keepDoing: "Keep doing",
    tryNext: "Try next",
    unverified: "Not enough speech to score this.",

    scorecardHeading: "Speech scorecard",
    scorecardBody:
      "Scores reflect this answer at the level you chose. Each coaching judgement is checked against your transcript.",
    percentageScale: "Scores out of 100",
    rankExcellent: "Excellent",
    rankStrong: "Strong",
    rankDeveloping: "Developing",
    rankFocus: "Focus next",

    deliveryHeading: "Delivery",
    paceLabel: "Pace",
    pausesLabel: "Pauses",
    fillersLabel: "Filler candidates",
    wordsLabel: "Words",
    speechUnitsLabel: "Speech units",
    paceUnavailable: "Not enough speech for a pace estimate",
    paceNotCompared: "Not compared for this language",
    noLongPauses: "No long pauses",
    /** e.g. "3 long pauses". Singular and plural differ by more than an "s" in
     * other languages, but the interface language is English by decision. */
    longPauses: (count: number) => `${count} long pause${count === 1 ? "" : "s"}`,

    fillersSubheading: "Likely fillers",
    fillersNote:
      "We count only words we recognised clearly and that often act as fillers in this language.",
    fillersEmpty: "None detected",
    repetitionSubheading: "Repeated wording",
    repetitionNote: "Common words such as articles and prepositions are left out.",
    repetitionEmpty: "No notable repetition",

    messageHeading: "Message",
    relevanceLabel: "Relevance",
    structureLabel: "Structure",

    languageHeading: "Language",
    grammarLabel: "Grammar",
    vocabularyLabel: "Vocabulary",
    preciseWordingHeading: "Precise wording worth keeping",

    sharperVersionHeading: "One clearer way to say it",

    pronunciationHeading: "Pronunciation",
    pronunciationProcessing: "We are still checking pronunciation. Your main feedback is ready.",
    pronunciationScoreLabel: "Pronunciation",
    accuracyScoreLabel: "Sound accuracy",
    fluencyScoreLabel: "Fluency",
    prosodyScoreLabel: "Rhythm & intonation",
    wordsToReviewHeading: "Words to try again",
    syllablesLabel: "Syllables",
    soundsLabel: "Sounds",
    transcriptHeading: "Your transcript",
    transcriptNote: "This is what ChickyTutor heard. Faded words were less certain.",
    transcriptLabel: "Attempt transcript",
    lowConfidenceTitle: "Lower transcription confidence",

    retryPrompt: "Retry this prompt",
    newPrompt: "New prompt",
  },
} as const;
